import catalog from "assets/data/products";

import self from "../../index";
import { getColorLabel } from "../helpers/colorHelper";
import { getLocationName } from "../helpers/locationHelper";
import { buyPriceOperation, removeMarginFromSellPrice, round, marginCalculation } from "../helpers/priceHelper";
import { getFinishedArticles, buildArticleReference } from "../helpers/projectHelper";
import store from "../store";
import BatGenerator from "./BatGenerator";
import {
    CUSTOMIZATION_TYPE_IMAGE,
    CUSTOMIZATION_TYPE_TEXT,
    PRODUCT_FINISHING,
    PRODUCT_FINISHING_EMBROIDERY,
    PRODUCT_FINISHING_PRINTING,
} from "./constants";

const batGenerator = new BatGenerator();

const {
    modules: { iframeApi },
} = self.app;

function getLocationPrices(prices, finishing, type) {
    let locationPrice = {};

    if (type === CUSTOMIZATION_TYPE_TEXT) {
        if (finishing === PRODUCT_FINISHING_EMBROIDERY) {
            locationPrice = prices.textEmbroideryQuantities;
        } else if (finishing === PRODUCT_FINISHING_PRINTING) {
            locationPrice = prices.textPrintingQuantities;
        }
    } else if (type === CUSTOMIZATION_TYPE_IMAGE) {
        if (finishing === PRODUCT_FINISHING_EMBROIDERY) {
            locationPrice = prices.textEmbroideryQuantities;
        } else if (finishing === PRODUCT_FINISHING_PRINTING) {
            locationPrice = prices.textPrintingQuantities;
        }
    }

    return locationPrice;
}

function getQuantityDecreasingPart(prices, customAmount) {
    // If amount is inferior to quantity part or if it's the last loop (max quantity part value)
    return Object.keys(prices).find((qty, index) => customAmount <= qty || Object.keys(prices).length - 1 === index);
}

function getTotalBuyPriceArticles(article, product) {
    const { quantity: qty, size, color } = article;
    const productPrice = product.buyPrices[size][color].price;
    const price = round(marginCalculation(productPrice, product.marge), 2);
    return price * qty;
}

function getTotalBuyPriceByCustomizations(article, product) {
    return article.customization.reduce((acc, custom) => {
        const { prices } = product.locations[custom.location];
        const customizationPrices = getLocationPrices(prices, article.finishing, custom.type);

        const quantityPart = getQuantityDecreasingPart(customizationPrices, article.totalQuantity);
        const customPrice = customizationPrices[quantityPart];
        const price = round(marginCalculation(customPrice, product.marge), 2);

        return acc + price * article.quantity;
    }, 0);
}

function getCodeProduct(finishing, codes) {
    return finishing === PRODUCT_FINISHING_PRINTING ? codes.printing : codes.embroidery;
}

function getArticlesTotalPrice(articles) {
    return articles.reduce((acc, art) => acc + art.PV, 0);
}

function pricesCalculation(articles) {
    return articles.map((article) => {
        const quoteArt = { ...article };
        const product = catalog.find((prod) => prod.catalogId === quoteArt.catalogId);

        const totalPriceArticles = getTotalBuyPriceArticles(quoteArt, product);

        const totalPriceCustomizations = getTotalBuyPriceByCustomizations(quoteArt, product);

        quoteArt.PV = buyPriceOperation(totalPriceArticles, totalPriceCustomizations);
        if (!quoteArt.PV || quoteArt.PV <= 0) {
            throw new Error("Price is undefined");
        }
        quoteArt.PR = removeMarginFromSellPrice(quoteArt.PV, product.marge);
        quoteArt.PA = quoteArt.PR;

        quoteArt.code = getCodeProduct(quoteArt.finishing, product.legallaisCode);

        return quoteArt;
    });
}

function cleanCustomization(articles) {
    return articles.map((article) => {
        article.customization = article.customization.map((cstm) => ({
            location: cstm.location,
            locationLabel: getLocationName(cstm.location),
            type: cstm.type,
            hideImageBackground: cstm.hideImageBackground,
        }));
        return article;
    });
}

function addLabels(articles) {
    return articles.map((article) => ({
        ...article,
        finishingLabel: PRODUCT_FINISHING[article.finishing],
        colorLabel: getColorLabel(article.color),
    }));
}

function roundArticlesPrices(articles) {
    return articles.map((article) => {
        const quoteArt = { ...article };

        quoteArt.PA = round(quoteArt.PA, 4);
        quoteArt.PR = round(quoteArt.PR, 4);
        quoteArt.PV = round(quoteArt.PV, 4);

        return quoteArt;
    });
}

function expandArticlesBySize(articles) {
    return articles.reduce((expandedArticles, article) => {
        const sizes = Object.keys(article.quantity).filter((size) => article.quantity[size] > 0);
        const totalQuantity = sizes.reduce((sum, size) => sum + article.quantity[size], 0);

        const sizeArticles = sizes.map((size) => {
            const qty = article.quantity[size];
            const reference = buildArticleReference(article.catalogId, size, article.color);

            return {
                ...article,
                size,
                quantity: qty,
                totalQuantity,
                reference,
            };
        });

        return expandedArticles.concat(sizeArticles);
    }, []);
}

function URIEncode(string) {
    return encodeURIComponent(string).replace(/\(/g, "%28").replace(/\)/g, "%29");
}

async function hashQuote(quoteComposition) {
    const message = URIEncode(JSON.stringify(quoteComposition));
    const msgUint8 = new TextEncoder().encode(`${message}${process.env.VUE_SECRET_HASH}`);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    quoteComposition.hash = hashHex;
    return quoteComposition;
}

export default async function generateQuote(project) {
    const quoteComposition = { ...project };
    let { articles } = quoteComposition;

    articles = getFinishedArticles(articles);
    articles = expandArticlesBySize(articles);
    articles = batGenerator.generate(articles);
    articles = pricesCalculation(articles);
    articles = cleanCustomization(articles);
    articles = addLabels(articles);
    articles = roundArticlesPrices(articles);

    quoteComposition.articles = articles;

    quoteComposition.price = round(getArticlesTotalPrice(quoteComposition.articles), 4);

    try {
        const quote = await store.dispatch("project/saveQuote", quoteComposition);
        console.log("quote", quote);
        // await fetch quote, get quote with hash
        const quoteCompositionWithHash = await hashQuote(quote.composition);
        const uriEncodedComposition = URIEncode(JSON.stringify(quoteCompositionWithHash));
        iframeApi.sendEvent("quoteGenerated", uriEncodedComposition);
    } catch (error) {
        throw new Error(error);
    }
}
